import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Dropdown, Form } from "capitalrx-components-2.0/lib/forms";
import { Modal } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, useResponsive } from "capitalrx-components-2.0/lib/layout";

import { ThemedButton, ThemedRadioGroup } from "@/App/components";
import { monthOptions } from "@/App/constants/constants";

export interface GenerateClaimsForm {
  readonly spanType: "ytd" | "past-month" | "past-year";
  readonly month: string;
  readonly year: string;
}

export interface GeneratePDFModalProps {
  readonly visible: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (formData: GenerateClaimsForm) => Promise<void>;
  readonly claimsType: "medical" | "pharmacy";
}

export const GeneratePDFModal = ({ visible, onClose, onSubmit, claimsType }: GeneratePDFModalProps) => {
  const { t: translate } = useTranslation();
  const generatePDFFormDefaultValues: GenerateClaimsForm = useMemo(() => {
    return {
      spanType: "ytd",
      month: "",
      year: new Date().getFullYear().toString(),
    };
  }, []);

  const generatePDFForm = useForm<GenerateClaimsForm>({
    defaultValues: { ...generatePDFFormDefaultValues },
  });

  const generateSpanType = generatePDFForm.watch("spanType");

  const yearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    return [...Array(100).keys()].map((v) => now - v);
  }, []);

  useEffect(() => {
    if (visible) {
      generatePDFForm.reset({ ...generatePDFFormDefaultValues });
    }
  }, [visible]);

  useEffect(() => {
    if (generateSpanType !== generatePDFFormDefaultValues["spanType"]) {
      generatePDFForm.resetField("month");
    }
  }, [generateSpanType]);

  const { responsiveColumns } = useResponsive({
    responsiveColumns: {
      mobile: 12,
      desktop: 6,
    },
  });

  let title = translate("CLAIMS.generatePDFModal.generatePharmacyClaimsHistoryModalTitle");
  if(claimsType === "medical") {
    title = translate("CLAIMS.generatePDFModal.generateMedicalClaimsHistoryModalTitle");
  }

  return (
    <Modal
      testId="generate-claims-history"
      title={title}
      footer={
        <Shelf spacing="small">
          <Shelf.Spacer />
          <ThemedButton appearance="secondary" onClick={onClose}>
            {translate("CLAIMS.generatePDFModal.cancel")}
          </ThemedButton>
          <ThemedButton appearance="primary" onClick={() => generatePDFForm.handleSubmit(onSubmit)()}>
            {translate("CLAIMS.generatePDFModal.generate")}
          </ThemedButton>
        </Shelf>
      }
      visible={visible}
      onClose={onClose}
    >
      <Form form={generatePDFForm} onSubmit={onSubmit} testId="generate-pdf">
        <Grid>
          <Grid.Span columns={12}>
            <ThemedRadioGroup.Field
              testId="generate-history-selection"
              label={translate("CLAIMS.generatePDFModal.generateHistorySelectionLabel")}
              name="spanType"
              required
            >
              <ThemedRadioGroup.Option value="ytd">
                {translate("CLAIMS.generatePDFModal.generateHistorySelectionOption1")}
              </ThemedRadioGroup.Option>
              <ThemedRadioGroup.Option value="past-year">
                {translate("CLAIMS.generatePDFModal.generateHistorySelectionOption2")}
              </ThemedRadioGroup.Option>
              <ThemedRadioGroup.Option value="past-month">
                {translate("CLAIMS.generatePDFModal.generateHistorySelectionOption3")}
              </ThemedRadioGroup.Option>
            </ThemedRadioGroup.Field>
          </Grid.Span>
          {(generateSpanType === "past-month" || generateSpanType === "past-year") && (
            <Grid.Span columns={responsiveColumns}>
              <Dropdown.Field testId="year-selection" required name="year" label={translate("CLAIMS.generatePDFModal.year")}>
                {yearOptions.map((y) => {
                  return (
                    <Dropdown.Option value={y.toString()} key={y}>
                      {y}
                    </Dropdown.Option>
                  );
                })}
              </Dropdown.Field>
            </Grid.Span>
          )}
          {generateSpanType === "past-month" && (
            <Grid.Span columns={responsiveColumns}>
              <Dropdown.Field testId="months-selection" required name="month" label={translate("CLAIMS.generatePDFModal.month")}>
                {monthOptions.map((month: { value: number; label: string }) => {
                  return (
                    <Dropdown.Option value={month["value"]} key={month["value"]}>
                      {month["label"]}
                    </Dropdown.Option>
                  );
                })}
              </Dropdown.Field>
            </Grid.Span>
          )}
        </Grid>
      </Form>
    </Modal>
  );
};
